import { useState, useEffect, useCallback, useRef } from 'react';
import { adminAPI } from '../../lib/api';
import RichTextEditor from '../RichTextEditor';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Plus, Trash2, Edit2, Send, Clock, CheckCircle2,
  Linkedin, Twitter, Facebook, RefreshCw,
  CalendarClock, Zap, ChevronDown, Upload, X as XIcon,
  ExternalLink, Loader2, ImageIcon, Sparkles,
} from 'lucide-react';

// ── Social card templates ──────────────────────────────────────────────────────
const TEMPLATES = [
  { id: 'auto',      label: 'Auto',      desc: 'Detect from content' },
  { id: 'stat',      label: 'Stat',      desc: 'Large number / % highlight' },
  { id: 'quote',     label: 'Quote',     desc: 'Narrative / reflective text' },
  { id: 'explainer', label: 'Explainer', desc: 'Checklist / feature list' },
  { id: 'community', label: 'Community', desc: 'Engagement / question post' },
];

// ── Channel + status config ────────────────────────────────────────────────────

const CHANNELS = [
  { id: 'linkedin', label: 'LinkedIn',   Icon: Linkedin, color: 'text-[#0A66C2]', bg: 'bg-[#0A66C2]/10' },
  { id: 'facebook', label: 'Facebook',   Icon: Facebook, color: 'text-[#1877F2]', bg: 'bg-[#1877F2]/10' },
  { id: 'x',        label: 'X / Twitter', Icon: Twitter, color: 'text-slate-800', bg: 'bg-slate-100' },
];

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700'   },
  published:  { label: 'Published', color: 'bg-green-100 text-green-700' },
  failed:     { label: 'Failed',    color: 'bg-red-100 text-red-700'     },
  draft:      { label: 'Draft',     color: 'bg-slate-100 text-slate-600' },
};

const EMPTY_FORM = {
  content:       '',
  channels:      ['linkedin', 'facebook', 'x'],
  scheduled_for: '',
  schedule_type: 'now',
  image_url:     '',
  notes:         '',
  status:        'scheduled',
};

// ── Small helpers ─────────────────────────────────────────────────────────────

function getDomain(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return url; }
}

function ChannelBadges({ channels }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {CHANNELS.filter(c => channels?.includes(c.id)).map(({ id, Icon, color, bg }) => (
        <span key={id} className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${bg} ${color}`}>
          <Icon className="w-3 h-3" />
        </span>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const SocialQueueTab = () => {
  const [posts, setPosts]                 = useState([]);
  const [loading, setLoading]             = useState(true);
  const [statusFilter, setStatusFilter]   = useState('all');
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [submitting, setSubmitting]       = useState(false);
  const [editPost, setEditPost]           = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [imageUploading, setImageUploading]     = useState(false);
  const [imageGenerating, setImageGenerating]   = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('auto');

  // Link preview
  const [linkPreview, setLinkPreview]   = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const lastFetchedUrl  = useRef(null);
  const linkDebounceRef = useRef(null);

  // Hidden file input
  const fileInputRef = useRef(null);

  // ── Fetch posts ─────────────────────────────────────────────────────────────

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await adminAPI.listSocialQueue(params);
      setPosts(res.data || []);
    } catch {
      toast.error('Failed to load social queue');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // ── Debounced link preview ──────────────────────────────────────────────────

  useEffect(() => {
    const urlMatch = form.content.match(/https?:\/\/[^\s"'<>]+/);
    const url = urlMatch ? urlMatch[0] : null;

    if (!url) {
      setLinkPreview(null);
      lastFetchedUrl.current = null;
      clearTimeout(linkDebounceRef.current);
      return;
    }

    // Same URL already fetched / dismissed
    if (lastFetchedUrl.current === url) return;

    clearTimeout(linkDebounceRef.current);
    linkDebounceRef.current = setTimeout(async () => {
      if (lastFetchedUrl.current === url) return;
      lastFetchedUrl.current = url;
      setPreviewLoading(true);
      try {
        const res = await adminAPI.getSocialOgPreview(url);
        if (res.data) setLinkPreview(res.data);
      } catch {
        // silently ignore network errors
      } finally {
        setPreviewLoading(false);
      }
    }, 800);

    return () => clearTimeout(linkDebounceRef.current);
  }, [form.content]);

  // ── Image upload ────────────────────────────────────────────────────────────

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const res = await adminAPI.uploadSocialImage(file);
      setForm(f => ({ ...f, image_url: res.data.url }));
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setImageUploading(false);
      e.target.value = '';
    }
  };

  // ── Image generation ────────────────────────────────────────────────────────

  const handleGenerateImage = async () => {
    const text = form.content.replace(/\*\*?|__?|#{1,6}\s/g, '').trim();
    if (!text) { toast.error('Write some post content first'); return; }
    setImageGenerating(true);
    try {
      const res = await adminAPI.generateSocialImage(text, selectedTemplate);
      setForm(f => ({ ...f, image_url: res.data.url }));
      toast.success(`Graphic generated (${res.data.template_used} template)`);
    } catch {
      toast.error('Image generation failed — try again');
    } finally {
      setImageGenerating(false);
    }
  };

  // ── Form helpers ────────────────────────────────────────────────────────────

  const toggleChannel = (id) => {
    setForm(f => ({
      ...f,
      channels: f.channels.includes(id)
        ? f.channels.filter(c => c !== id)
        : [...f.channels, id],
    }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setLinkPreview(null);
    lastFetchedUrl.current = null;
  };

  const openEdit = (post) => {
    setEditPost(post);
    setLinkPreview(null);
    lastFetchedUrl.current = null;
    const hasTime = !!post.scheduled_for;
    setForm({
      content:       post.content || '',
      channels:      post.channels || ['linkedin', 'facebook', 'x'],
      scheduled_for: hasTime ? new Date(post.scheduled_for).toISOString().slice(0, 16) : '',
      schedule_type: hasTime ? 'scheduled' : 'now',
      image_url:     post.image_url || '',
      notes:         post.notes || '',
      status:        post.status || 'scheduled',
    });
  };

  const cancelEdit = () => { setEditPost(null); resetForm(); };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) { toast.error('Post content is required'); return; }
    if (form.channels.length === 0) { toast.error('Select at least one channel'); return; }

    const payload = {
      content:       form.content.trim(),
      channels:      form.channels,
      scheduled_for: form.schedule_type === 'scheduled' && form.scheduled_for
        ? new Date(form.scheduled_for).toISOString()
        : null,
      image_url: form.image_url || '',
      notes:     form.notes || '',
      status:    'scheduled',
    };

    setSubmitting(true);
    try {
      if (editPost) {
        await adminAPI.updateSocialPost(editPost.id, payload);
        toast.success('Post updated');
        setEditPost(null);
      } else {
        await adminAPI.createSocialPost(payload);
        toast.success('Post added to queue');
      }
      resetForm();
      fetchPosts();
    } catch {
      toast.error(editPost ? 'Failed to update post' : 'Failed to add post');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────

  const handleDelete = async (post) => {
    try {
      await adminAPI.deleteSocialPost(post.id);
      toast.success('Post deleted');
      setDeleteConfirm(null);
      fetchPosts();
    } catch {
      toast.error('Failed to delete post');
    }
  };

  // ── Status change ───────────────────────────────────────────────────────────

  const handleStatusChange = async (post, newStatus) => {
    try {
      await adminAPI.updateSocialPostStatus(post.id, newStatus);
      toast.success(`Marked as ${newStatus}`);
      fetchPosts();
    } catch {
      toast.error('Failed to update status');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const charCount = form.content.replace(/\*\*?|__?|##?\s/g, '').length;
  const isEdit    = !!editPost;

  return (
    <div className="space-y-8">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-heading font-bold text-2xl text-dark-slate">Social Media Queue</h2>
          <p className="text-slate-gray text-sm mt-1">
            Compose posts, choose channels and schedule times — posts queue for automated publishing.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPosts} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* ── Compose / Edit form ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-heading font-semibold text-dark-slate mb-5 flex items-center gap-2">
          {isEdit
            ? <><Edit2 className="w-4 h-4 text-blue-500" /> Edit Post</>
            : <><Plus className="w-4 h-4 text-green-600" /> Compose New Post</>}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* ── Content + formatting toolbar ─────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-semibold text-dark-slate">
                Post Content <span className="text-red-500">*</span>
              </label>
              <span className={`text-xs ${charCount > 280 ? 'text-amber-500 font-medium' : 'text-slate-400'}`}>
                {charCount} chars
                {charCount > 280 && ' · over X/Twitter limit (280)'}
              </span>
            </div>
            <RichTextEditor
              value={form.content}
              onChange={val => setForm(f => ({ ...f, content: val }))}
              placeholder="Write your post here…"
              minHeight={160}
            />
            <p className="text-[11px] text-slate-400 mt-1.5">
              Formatting: <strong>**bold**</strong>, <em>*italic*</em>, bullet lists, and links work great on LinkedIn. Facebook & X render plain text.
            </p>
          </div>

          {/* ── Link preview ─────────────────────────────────────────────── */}
          {(previewLoading || linkPreview) && (
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
              {previewLoading ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Fetching link preview…
                </div>
              ) : linkPreview && (
                <div className="flex gap-3 p-3">
                  {linkPreview.image ? (
                    <img
                      src={linkPreview.image}
                      alt=""
                      className="w-20 h-16 rounded-lg object-cover shrink-0 border border-slate-200"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-20 h-16 rounded-lg bg-slate-200 shrink-0 flex items-center justify-center">
                      <ExternalLink className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {linkPreview.title && (
                      <p className="text-sm font-semibold text-dark-slate line-clamp-1">{linkPreview.title}</p>
                    )}
                    {linkPreview.description && (
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{linkPreview.description}</p>
                    )}
                    <a
                      href={linkPreview.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-blue-500 mt-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {getDomain(linkPreview.url)}
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setLinkPreview(null); lastFetchedUrl.current = '__dismissed__'; }}
                    className="text-slate-300 hover:text-slate-500 shrink-0 self-start mt-0.5"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Image: generate or upload ────────────────────────────────── */}
          <div>
            <label className="text-sm font-semibold text-dark-slate block mb-2">
              Image <span className="text-slate-400 font-normal text-xs">(optional — attached image on social)</span>
            </label>

            {form.image_url ? (
              /* Preview of generated / uploaded image */
              <div className="flex items-start gap-4">
                <div className="relative">
                  <img
                    src={form.image_url}
                    alt="Post graphic"
                    className="h-40 rounded-xl object-cover border border-slate-200 shadow-sm"
                    style={{ aspectRatio: '1200/630' }}
                  />
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, image_url: '' }))}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-sm transition-colors"
                  >
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleGenerateImage}
                  disabled={imageGenerating}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg transition-all disabled:opacity-50"
                >
                  {imageGenerating
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Regenerating…</>
                    : <><Sparkles className="w-3.5 h-3.5" /> Regenerate</>}
                </button>
              </div>
            ) : (
              /* Generate + upload buttons */
              <div className="space-y-3">
                {/* Template selector */}
                <div className="flex flex-wrap gap-1.5">
                  {TEMPLATES.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      title={t.desc}
                      onClick={() => setSelectedTemplate(t.id)}
                      className={`px-3 py-1 text-xs font-medium rounded-full border transition-all
                        ${selectedTemplate === t.id
                          ? 'bg-violet-600 text-white border-violet-600'
                          : 'bg-white text-slate-500 border-slate-200 hover:border-violet-300 hover:text-violet-600'}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleGenerateImage}
                    disabled={imageGenerating || imageUploading}
                    className="flex items-center gap-2 px-5 py-3 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    {imageGenerating
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                      : <><Sparkles className="w-4 h-4" /> Auto-generate graphic</>}
                  </button>

                  <span className="text-xs text-slate-400">or</span>

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={imageUploading || imageGenerating}
                    className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-xl text-sm text-slate-500 hover:border-slate-400 hover:text-slate-600 hover:bg-slate-50/60 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {imageUploading
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                      : <><Upload className="w-4 h-4" /> Upload image</>}
                  </button>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handleImageUpload}
            />
          </div>

          {/* ── Channels ─────────────────────────────────────────────────── */}
          <div>
            <label className="text-sm font-semibold text-dark-slate block mb-2">
              Publish to <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-3">
              {CHANNELS.map(({ id, label, Icon, color, bg }) => {
                const checked = form.channels.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleChannel(id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all
                      ${checked
                        ? `border-current ${color} ${bg}`
                        : 'border-slate-200 text-slate-400 bg-white hover:border-slate-300'}`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                    {checked && <CheckCircle2 className="w-3.5 h-3.5 ml-1" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Schedule ─────────────────────────────────────────────────── */}
          <div>
            <label className="text-sm font-semibold text-dark-slate block mb-2">Schedule</label>
            <div className="flex gap-3 mb-3">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, schedule_type: 'now' }))}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all
                  ${form.schedule_type === 'now'
                    ? 'border-green-500 text-green-700 bg-green-50'
                    : 'border-slate-200 text-slate-500 bg-white hover:border-slate-300'}`}
              >
                <Zap className="w-4 h-4" /> As soon as possible
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, schedule_type: 'scheduled' }))}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all
                  ${form.schedule_type === 'scheduled'
                    ? 'border-blue-500 text-blue-700 bg-blue-50'
                    : 'border-slate-200 text-slate-500 bg-white hover:border-slate-300'}`}
              >
                <CalendarClock className="w-4 h-4" /> Set date & time
              </button>
            </div>
            {form.schedule_type === 'scheduled' && (
              <Input
                type="datetime-local"
                value={form.scheduled_for}
                onChange={e => setForm(f => ({ ...f, scheduled_for: e.target.value }))}
                className="max-w-xs text-sm"
              />
            )}
          </div>

          {/* ── Internal notes ────────────────────────────────────────────── */}
          <div>
            <label className="text-sm font-medium text-slate-gray block mb-1">
              Internal notes <span className="text-slate-400 text-xs">(optional)</span>
            </label>
            <Input
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. Requested by Kellye Blackburn"
              className="text-sm max-w-md"
            />
          </div>

          {/* ── Actions ──────────────────────────────────────────────────── */}
          <div className="flex gap-3 pt-1">
            <Button type="submit" disabled={submitting || imageUploading} className="bg-green-600 hover:bg-green-700 text-white gap-2">
              <Send className="w-4 h-4" />
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Add to Queue'}
            </Button>
            {isEdit && (
              <Button type="button" variant="outline" onClick={cancelEdit}>Cancel</Button>
            )}
          </div>
        </form>
      </div>

      {/* ── Queue table ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-heading font-semibold text-dark-slate flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" /> Queue
            <span className="text-xs text-slate-400 font-normal ml-1">({posts.length})</span>
          </h3>

          <div className="flex gap-1.5 flex-wrap">
            {['all', 'scheduled', 'published', 'failed', 'draft'].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs px-3 py-1 rounded-full font-medium capitalize transition-colors
                  ${statusFilter === s
                    ? 'bg-dark-slate text-white'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-400 text-sm">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="py-16 text-center">
            <Send className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No posts in queue.</p>
            <p className="text-slate-300 text-xs mt-1">Compose a post above to get started.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {posts.map(post => (
              <PostRow
                key={post.id}
                post={post}
                onEdit={() => openEdit(post)}
                onDelete={() => setDeleteConfirm(post)}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Delete confirm dialog ────────────────────────────────────────────── */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Post?</DialogTitle>
          </DialogHeader>
          {deleteConfirm && (
            <p className="text-sm text-slate-gray line-clamp-3">
              "{deleteConfirm.content?.slice(0, 120)}…"
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handleDelete(deleteConfirm)}
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Post row ──────────────────────────────────────────────────────────────────

function PostRow({ post, onEdit, onDelete, onStatusChange }) {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const scheduledDate = post.scheduled_for ? new Date(post.scheduled_for) : null;
  const isPast        = scheduledDate && scheduledDate < new Date();

  return (
    <div className="flex items-start gap-4 px-6 py-4 hover:bg-slate-50/50 transition-colors">

      {/* Image thumbnail */}
      {post.image_url && (
        <img
          src={post.image_url}
          alt=""
          className="w-14 h-14 rounded-xl object-cover shrink-0 border border-slate-100"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}
      {!post.image_url && (
        <div className="w-14 h-14 rounded-xl bg-slate-50 border border-slate-100 shrink-0 flex items-center justify-center">
          <ImageIcon className="w-5 h-5 text-slate-200" />
        </div>
      )}

      {/* Content + meta */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-dark-slate leading-relaxed line-clamp-3 mb-2">
          {post.content}
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <ChannelBadges channels={post.channels} />
          <StatusBadge status={post.status} />
          {scheduledDate ? (
            <span className={`flex items-center gap-1 text-xs ${isPast && post.status === 'scheduled' ? 'text-red-500 font-medium' : 'text-slate-400'}`}>
              <Clock className="w-3 h-3" />
              {isPast && post.status === 'scheduled' ? 'Overdue · ' : ''}
              {format(scheduledDate, 'MMM d, yyyy · h:mm a')}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-slate-400">
              <Zap className="w-3 h-3" /> ASAP
            </span>
          )}
          {post.notes && (
            <span className="text-xs text-slate-400 italic">Note: {post.notes}</span>
          )}
          <span className="text-xs text-slate-300">
            Added {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs gap-1 text-slate-500 h-8 px-2"
            onClick={() => setShowStatusMenu(v => !v)}
          >
            Status <ChevronDown className="w-3 h-3" />
          </Button>
          {showStatusMenu && (
            <div className="absolute right-0 top-8 z-10 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[140px]">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  className="w-full text-left text-xs px-3 py-2 hover:bg-slate-50 capitalize flex items-center gap-2"
                  onClick={() => { onStatusChange(post, key); setShowStatusMenu(false); }}
                >
                  <span className={`w-2 h-2 rounded-full ${cfg.color.split(' ')[0]}`} />
                  {cfg.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <Button variant="ghost" size="sm" className="text-slate-400 hover:text-blue-600 h-8 w-8 p-0" onClick={onEdit}>
          <Edit2 className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-600 h-8 w-8 p-0" onClick={onDelete}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default SocialQueueTab;
